import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Bell, BookOpen, CircleHelp, Keyboard, LogOut, Menu, PlayCircle, UserRound, X } from 'lucide-react';
import { useOpenAlerts } from '@/lib/nedarimData';
import { TEMPLATE_LABELS_HE } from '@shared/permissions.ts';
import { useAuth } from '@/auth/AuthProvider';
import { usePermissions } from '@/auth/usePermissions';
import { useGeneralSettings } from '@/lib/useShulSettings';
import { USE_EMULATORS } from '@/lib/firebase';
import { dateLabels } from '@/lib/dates';
import { NAV, NAV_GROUP_LABELS, navVisible, type NavItem } from './nav';
import * as DrawerPrimitive from '@radix-ui/react-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { Icon3D } from '@/components/Icon3D';
import { PersonAutocomplete } from '@/components/PersonAutocomplete';
import { InstallBanner } from '@/components/InstallBanner';
import { DaatanCredit } from '@/components/DaatanCredit';
import { HelpDialog } from '@/components/HelpDialog';
import { videoForPath } from '@shared/training.ts';

function useVisibleNav(): NavItem[] {
  const perms = usePermissions();
  return NAV.filter((n) => navVisible(n, perms));
}

function SideNav({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const groups = (['main', 'shul', 'finance', 'admin'] as const).map((g) => ({ g, items: items.filter((i) => i.group === g) })).filter((x) => x.items.length);
  return (
    <nav className="flex flex-col gap-5" aria-label="תפריט ראשי">
      {groups.map(({ g, items: its }) => (
        <div key={g} className="flex flex-col gap-0.5">
          {NAV_GROUP_LABELS[g] ? <div className="px-3 pb-1 text-xs font-semibold tracking-wide text-white/70">{NAV_GROUP_LABELS[g]}</div> : null}
          {its.map((n) => (
            <NavLink
              key={n.path}
              to={n.path}
              end={n.path === '/'}
              onClick={onNavigate}
              className={({ isActive }) => cn(
                'flex h-11 items-center gap-3 rounded-xl px-2 text-[15px] transition-colors',
                isActive ? 'bg-white/15 font-semibold text-white shadow-[inset_-3px_0_0_var(--color-gold)]' : 'text-white/80 hover:bg-white/10 hover:text-white',
              )}
            >
              {({ isActive }) => (
                <>
                  {/* 3D icons sit on a small stone tile: they are drawn for light backgrounds */}
                  <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg bg-bg/95', isActive && 'ring-2 ring-gold')}>
                    <Icon3D name={n.icon3d} size={24} eager />
                  </span>
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
      {/* help and the guide are open to everyone (each training video by permission) */}
      <div className="flex flex-col gap-0.5">
        <NavLink to="/help" onClick={onNavigate}
          className={({ isActive }) => cn('flex h-11 items-center gap-3 rounded-xl px-2 text-[15px] transition-colors', isActive ? 'bg-white/15 font-semibold text-white' : 'text-white/80 hover:bg-white/10 hover:text-white')}>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-bg/95"><PlayCircle className="size-5 text-primary" /></span>
          עזרה והדרכה
        </NavLink>
        <NavLink to="/guide" onClick={onNavigate}
          className={({ isActive }) => cn('flex h-11 items-center gap-3 rounded-xl px-2 text-[15px] transition-colors', isActive ? 'bg-white/15 font-semibold text-white' : 'text-white/80 hover:bg-white/10 hover:text-white')}>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-bg/95"><BookOpen className="size-5 text-primary" /></span>
          מדריך למערכת
        </NavLink>
      </div>
    </nav>
  );
}

function UserMenu() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  // Clear the "return to" page so the next person to log in starts at home.
  const logout = async () => { await signOut(); navigate('/login', { replace: true, state: null }); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex h-11 items-center gap-2 rounded-xl px-2 hover:bg-surface-2" aria-label="תפריט משתמש">
        <span className="flex size-8 items-center justify-center rounded-full bg-primary-soft text-primary"><UserRound className="size-4" /></span>
        <span className="hidden text-sm font-medium sm:inline">{profile?.displayName ?? ''}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>
          <div className="text-sm font-medium text-ink">{profile?.displayName}</div>
          <div className="ltr text-xs">{profile?.username}</div>
          {profile?.template ? <div className="mt-0.5">{TEMPLATE_LABELS_HE[profile.template]}</div> : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/help')}><PlayCircle /> עזרה והדרכה</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/guide')}><BookOpen /> מדריך למערכת</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/guide#shortcuts')}><Keyboard /> קיצורי מקלדת</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => { void logout(); }}><LogOut /> יציאה</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell() {
  const items = useVisibleNav();
  const { canRead } = usePermissions();
  const canReadPeople = canRead('people');
  const navigate = useNavigate();
  const { shortName, subtitle } = useGeneralSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const today = dateLabels(new Date());
  // the guide resolves the chapter of this screen itself (keeps the guide text out of the main bundle)
  const helpHref = `/guide?for=${encodeURIComponent(location.pathname)}`;
  // "?" opens the training video of this screen when there is one the user may watch; otherwise the guide chapter
  const screenVideo = videoForPath(location.pathname);
  const video = screenVideo && (!screenVideo.requires || canRead(screenVideo.requires)) ? screenVideo : null;
  const [helpOpen, setHelpOpen] = useState(false);
  const openHelp = () => { if (video) setHelpOpen(true); else navigate(helpHref); };

  // Global keyboard shortcuts (listed in the guide, chapter "קיצורי דרך"):
  //   Ctrl+K — person search · ? — this screen's training video (or its guide chapter) · Alt+Home — dashboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName));
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k' || (e.ctrlKey || e.metaKey) && e.code === 'KeyK') {
        const el = document.getElementById('global-person-search');
        if (el) { e.preventDefault(); el.focus(); }
        return;
      }
      if (typing || e.ctrlKey || e.metaKey) return;
      if (e.key === '?' && location.pathname !== '/guide') { e.preventDefault(); openHelp(); }
      if (e.altKey && e.key === 'Home') { e.preventDefault(); navigate('/'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // re-bound every render: openHelp depends on the screen

  return (
    <div className="min-h-dvh overflow-x-clip lg:grid lg:grid-cols-[16rem_1fr]">
      {/* Desktop sidebar (on the right in RTL) */}
      <aside className="no-print sticky top-0 hidden h-dvh flex-col gap-6 overflow-y-auto bg-primary p-4 text-white lg:flex">
        <div className="flex items-center gap-3 px-1 pt-2">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-bg p-1"><Icon3D name="logo-mark" size={40} eager /></span>
          <div className="min-w-0">
            <div className="font-display text-lg leading-tight font-bold">{shortName}</div>
            <div className="mt-0.5 text-xs text-white/70">{subtitle}</div>
          </div>
        </div>
        <SideNav items={items} />
        <DaatanCredit width={120} tone="dark" className="mt-auto pt-4 pb-2" />
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="no-print sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-surface/90 px-4 backdrop-blur lg:px-8">
          {/* Mobile: the menu opens as a drawer from the side (owner request 2026-09-28 — instead of a bottom bar) */}
          <button type="button" onClick={() => setMenuOpen(true)} className="-ms-2 flex size-11 shrink-0 items-center justify-center rounded-xl text-ink hover:bg-surface-2 lg:hidden" aria-label="פתיחת התפריט">
            <Menu className="size-6" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 lg:hidden">
              <Icon3D name="logo-mark" size={32} eager />
              <span className="truncate font-display font-bold">{shortName}</span>
            </div>
            <div className="hidden text-sm text-ink-2 lg:block">
              {today.weekday}, {today.hebrew} <span className="text-muted">· <span className="ltr">{today.gregorian}</span></span>
            </div>
          </div>
          {canReadPeople ? (
            <div className="hidden w-72 md:block">
              <PersonAutocomplete key={location.pathname} id="global-person-search" value={null} placeholder="חיפוש אדם… (Ctrl+K)" onChange={(id) => { if (id) navigate(`/people/${id}`); }} />
            </div>
          ) : null}
          {USE_EMULATORS ? <span data-env-badge className="rounded-full bg-warning-soft px-2 py-0.5 text-xs text-warning-ink">סביבת פיתוח</span> : null}
          <button type="button" onClick={openHelp} className="flex size-11 items-center justify-center rounded-xl text-ink-2 hover:bg-surface-2"
            aria-label={video ? 'סרטון הדרכה למסך הזה' : 'עזרה למסך הזה'} title={video ? 'סרטון הדרכה למסך הזה (מקש ?)' : 'עזרה למסך הזה (מקש ?)'}>
            <CircleHelp className="size-5" />
          </button>
          <AlertsBell />
          <UserMenu />
        </header>

        <main className="mx-auto w-full min-w-0 max-w-6xl flex-1 px-4 pt-5 pb-24 lg:px-8 lg:pb-10">
          <Outlet />
        </main>
      </div>

      <InstallBanner />
      {video ? <HelpDialog video={video} guideHref={helpHref} open={helpOpen} onOpenChange={setHelpOpen} /> : null}
      <MobileMenu open={menuOpen} onOpenChange={setMenuOpen} items={items} shortName={shortName} subtitle={subtitle} />
    </div>
  );
}

/** Open alerts count → alerts center. Financial readers only (canSeeAlerts) — the alerts are about people's money. */
function AlertsBell() {
  const { seeAlerts } = usePermissions();
  const allowed = seeAlerts;
  const alerts = useOpenAlerts(allowed);
  if (!allowed) return null;
  const n = alerts.data.length;
  const urgent = alerts.data.some((a) => a.severity === 'error');
  return (
    <NavLink to="/alerts" className="relative flex size-11 items-center justify-center rounded-xl text-ink-2 hover:bg-surface-2" aria-label={n ? `${n} התראות פתוחות` : 'אין התראות פתוחות'} title="מרכז התראות">
      <Bell className="size-5" />
      {n ? (
        <span className={cn('absolute -top-0.5 -end-0.5 min-w-5 rounded-full px-1 text-center text-[11px] leading-5 font-bold', urgent ? 'bg-danger text-white' : 'bg-gold text-ink')}>
          {n > 99 ? '99+' : n}
        </span>
      ) : null}
    </NavLink>
  );
}

/** Mobile menu: a drawer from the start side (right in RTL) with the whole menu; closes on navigation. */
function MobileMenu({ open, onOpenChange, items, shortName, subtitle }: {
  open: boolean; onOpenChange: (o: boolean) => void; items: NavItem[]; shortName: string; subtitle?: string;
}) {
  const location = useLocation();
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => { onOpenChange(false); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // Safety net: if a phone does not advance the slide-in (busy moment, power saving), finish it — the menu must never
  // stay off-screen until something else re-renders (owner report 2026-09-30).
  useEffect(() => {
    // both ways: opening (slide in) and closing (the drawer unmounts only when its slide-out ends)
    const t = setTimeout(() => { panel.current?.getAnimations?.().forEach((a) => { try { a.finish(); } catch { /* already done */ } }); }, 350);
    return () => clearTimeout(t);
  }, [open]);
  return (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="drawer-overlay fixed inset-0 z-50 bg-ink/40 backdrop-blur-[2px] lg:hidden" />
        <DrawerPrimitive.Content ref={panel} className="drawer-panel no-print fixed top-0 start-0 z-50 flex h-dvh w-[82vw] max-w-80 flex-col gap-5 overflow-y-auto overscroll-contain bg-primary p-4 pb-safe text-white shadow-2xl will-change-transform lg:hidden" aria-describedby={undefined}>
          <div className="flex items-center gap-3 px-1 pt-1">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-bg p-1"><Icon3D name="logo-mark" size={36} eager /></span>
            <div className="min-w-0 flex-1">
              <DrawerPrimitive.Title className="font-display text-lg leading-tight font-bold">{shortName}</DrawerPrimitive.Title>
              {subtitle ? <div className="mt-0.5 text-xs text-white/70">{subtitle}</div> : null}
            </div>
            <DrawerPrimitive.Close className="flex size-10 items-center justify-center rounded-lg text-white/80 hover:bg-white/10" aria-label="סגירת התפריט"><X className="size-5" /></DrawerPrimitive.Close>
          </div>
          <SideNav items={items} onNavigate={() => onOpenChange(false)} />
          <DaatanCredit width={120} tone="dark" className="mt-auto pt-2 pb-2" />
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}
